import { diagnoseCartLines, type BlockedCartLine } from '@/features/cart/cartAvailability';
import { minRequired } from '@/features/menu/optionRequirement';
import type { CartLine, Menu, MenuItem } from '@/types';

/** A cart line the menu, as it is NOW, no longer accepts as saved — plus,
 *  when the reason is a rule the waiter can still satisfy from the options
 *  sheet, the labels of the required groups the line has no (or too few)
 *  answers for. Empty unless `reason === 'optionRules'`. */
export interface StaleCartLine extends BlockedCartLine {
  missingGroups: string[];
}

/**
 * DF-0106 — the waiter's mirror of the guest cart's `diagnoseCartLines`,
 * for the case that hurt at Yuji on 2026-09-02: a required option group
 * created mid-service, on a product already in a tablet's cart. The tablet
 * had never refetched the menu, `TicketOptionsModal` could not ask a
 * question it did not know, and the quote refused the round nine times in
 * 50 s with nothing on screen naming the line.
 *
 * No period verdict: the waiter surface has no scheduling, `TicketPlats`
 * already filters what is offered by the current period, and a line that
 * slipped through is the quote's call, not this dialog's.
 */
export function diagnoseWaiterCart(
  lines: CartLine[],
  menu: Menu | undefined,
  kind: 'dine_in' | 'takeaway',
): StaleCartLine[] {
  const blocked = diagnoseCartLines({ lines, menu, mode: kind, remainingPeriods: new Set() });
  if (blocked.length === 0) return [];
  const itemsById = new Map((menu?.items ?? []).map((item) => [item.id, item]));
  const linesById = new Map(lines.map((line) => [line.lineId, line]));
  return blocked.map((b) => ({
    ...b,
    missingGroups:
      b.reason === 'optionRules'
        ? missingRequiredGroups(linesById.get(b.lineId), itemsById.get(b.itemId))
        : [],
  }));
}

function missingRequiredGroups(line: CartLine | undefined, item: MenuItem | undefined): string[] {
  if (!line || !item?.optionGroups) return [];
  const chosen = new Map<string, number>();
  for (const sel of line.selectedOptions) {
    const qty = sel.options.reduce((acc, o) => acc + o.qty, 0);
    chosen.set(sel.groupId, (chosen.get(sel.groupId) ?? 0) + qty);
  }
  return item.optionGroups
    .filter((g) => (chosen.get(g.id) ?? 0) < minRequired(g))
    .map((g) => g.label);
}

/** i18n key + params for one stale line — one wording for the cart sheet
 *  and the confirmation dialog, so the two never disagree. */
export function staleLineMessage(stale: StaleCartLine): [string, Record<string, string>] {
  if (stale.reason === 'optionRules') {
    return stale.missingGroups.length > 0
      ? ['waiter.ticket.stale.missing', { groups: stale.missingGroups.join(', ') }]
      : ['waiter.ticket.stale.rules', {}];
  }
  return [`waiter.ticket.stale.${stale.reason}`, {}];
}
